import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import { connect } from 'react-redux';
import { resizeCanvas } from '../../redux/actions/ui/canvas';
import './Editor.css';
import InteractiveLayerContainer from './../interactive-layer-container/InteractiveLayerContainer';
import {
  convertObjectToArray,
  GetActivePage,
  SetActivePage,
  getOriginalHeight,
  isImageInChange,
} from '../../redux/helpers';
import { setEditorLayout } from './../../redux/helpers/editor';
import { setIsActive } from '../../redux/actions/entities/pages';
import EditorBookPageNumbers from '../editor-book-page-numbers/EditorBookPageNumbers';
import {
  getPlaceholders,
  getBackgroundPlaceholders } from '../../redux/helpers/pages';
import { getLayoutsByType } from '../../redux/helpers/layouts';
import { addPageToViewed } from '../../redux/actions/ui/addPageToViewed';
import { disabelImageDropRule } from '../../helpers/dropHelper';


const editorTarget = {
  canDrop(props, monitor) {
    const { canDropImage } = props;
    const item = monitor.getItem();
    return canDropImage(item);
  },

  drop(props, monitor) {
    const item = monitor.getItem();
    const {
      dropImage,
      pages,
      isChange,
    } = props;

    const currentPage = GetActivePage(pages.byId);
    const coverLayouts = getLayoutsByType(props.layouts, 'isPhotoCover');
    const placeholderNumbers = [];
    coverLayouts.find((element) => {
      placeholderNumbers.push(element.placeholders.length);
    });

    const maximumDroppedImages = Math.max(...placeholderNumbers);
    if (currentPage.isCover && Object.keys(currentPage.droppedImages).length > (maximumDroppedImages - 1) && !isChange) {
      return undefined;
    }

    dropImage(item);

    return { moved: true };
  },
};

function collect(connect, monitor) {
  return {
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver(),
    isOverCurrent: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop(),
    itemType: monitor.getItemType(),
    isDropped: monitor.didDrop(),
  };
}

class Editor extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      pageStyle: {
        width: getOriginalHeight(this.props.bookProperties.bookSizes) * 2,
      },
    };
  }

  componentWillMount() {
    this.setCanvasSize();
    window.addEventListener('resize', this.setCanvasSize.bind(this));
  }

  shouldComponentUpdate(nextProps) {
    const isAutofillFinishedOrNormalBook = (nextProps.autofillFinished && nextProps.autofill) || !nextProps.autofill;
    if (isAutofillFinishedOrNormalBook) {
      return true;
    }

    return false;
  }

  setCanvasSize() {
    const {
      pages,
      layouts,
      bookProperties,
    } = this.props;
    const { byId } = pages;
    const currentPage = GetActivePage(byId);
    const editorLayout = setEditorLayout(currentPage, layouts, bookProperties.bookSizes);

    const {
      width,
      height,
    } = editorLayout;

    this.setState({
      pageStyle: {
        width,
      },
    });

    this.props.dispatch(resizeCanvas(width, height));
  }

  setPage = (pageId, currentPageId) => {
    this.props.dispatch(addPageToViewed(pageId));
    this.props.dispatch(addPageToViewed(currentPageId));
    this.props.dispatch(setIsActive(pageId));
  }

  nextPage = () => {
    const {
      isChange, pages, lang,
    } = this.props;
    const currentPage = GetActivePage(pages.byId);
    if (isImageInChange(isChange, currentPage.isCover, lang)) {
      return;
    }


    const {
      order,
      id: currentPageId,
    } = currentPage;

    const nextPageId = SetActivePage(order, pages);
    this.setPage(nextPageId, currentPageId);
  }

  prevPage = () => {
    const {
      isChange, pages, lang,
    } = this.props;
    const currentPage = GetActivePage(pages.byId);

    if (isImageInChange(isChange, currentPage.isCover, lang)) {
      return;
    }


    const {
      order,
      id: currentPageId,
    } = currentPage;


    const prevPageId = SetActivePage(order, pages, false);
    this.setPage(prevPageId, currentPageId);
  }

  editorScreen() {
    const {
      spine,
      pages,
      layouts,
      placeholders,
      images,
      allowRender,
      lang,
      bookProperties,
      texts,
      backgroundPlaceholders,
    } = this.props;
    const { byId, allIds } = pages;
    const page = GetActivePage(byId);
    const allIdsSize = allIds.length - 1;
    const pageNumber = page.order + 1;
    const {
      backgroundColor,
      layout: layoutId,
      id,
      backgroundImages,
    } = page;
    const { pageStyle } = this.state;
    const isPhotoCover = layouts[layoutId] ? layouts[layoutId].isPhotoCover : false;
    const editorStyle = setEditorLayout(page, layouts, bookProperties.bookSizes);
    const layoutPlaceholders = getPlaceholders(page, placeholders, pages, images);
    const layoutBackgroundPlaceholder = getBackgroundPlaceholders(page, backgroundPlaceholders, pages);
    const hasFlyLeaf = convertObjectToArray(byId).some(page => page.isFrontFlyLeaf === true);
    const coverLayoutWidth = layouts[layoutId] !== undefined ? layouts[layoutId].width : 0;
    const pageTexts = page.texts !== undefined ? Object.values(page.texts).map(element => texts[element]) : [];


    return (
      <div className="Editor" id="editor">
        { pageNumber > 1 ? <i className="fa fa-angle-left pager" onClick={this.prevPage} aria-hidden="true" /> : '' }
        <div className="EditorInner" style={editorStyle}>
          <InteractiveLayerContainer
            pageNumber={pageNumber}
            pages={byId}
            backgroundColor={backgroundColor}
            backgroundImage={backgroundImages ? backgroundImages.thumbnail : ''}
            spine={spine}
            placeholders={layoutPlaceholders}
            backgroundPlaceholders={!backgroundPlaceholders ? [] : layoutBackgroundPlaceholder}
            photoCover={isPhotoCover}
            allowRender={allowRender}
            lang={lang}
            coverLayoutWidth={coverLayoutWidth}
            activePageId={id}
            text={pageTexts}
            EditorLimit={editorStyle}
          />
        </div>
        <EditorBookPageNumbers
          pageNumber={pageNumber}
          style={pageStyle}
          page={page}
          photoCover={isPhotoCover}
          hasFlyLeaf={hasFlyLeaf}
          lang={lang}
          coverType={bookProperties.cover.type}
        />
        { (pageNumber - 1) !== allIdsSize ? <i className="fa fa-angle-right pager" onClick={this.nextPage} aria-hidden="true" /> : '' }

      </div>
    );
  }
  render() {
    const { byId } = this.props.pages;
    const { connectDropTarget, bookProperties, backgroundType } = this.props;
    const page = GetActivePage(byId);
    const { type } = bookProperties.cover;
    const { autofill } = bookProperties;

    if (!page) {
      return null;
    }
    // Disable image drop to editorScreen on flyleaf and consumer, exclusive  cover only
    if (disabelImageDropRule(type, autofill, page, backgroundType)) {
      return this.editorScreen();
    }
    
    return connectDropTarget(this.editorScreen());
  }
}


Editor = DropTarget('Image', editorTarget, collect)(Editor);

Editor.defaultProps = {
  pages: {},
  spine: {},
  layouts: {},
  isChange: false,
  placeholders: {},
  texts: {},
  lang: {},
  images: {},
  allowRender: false,
  backgroundType: '',
};

Editor.propTypes = {
  pages: PropTypes.object,
  spine: PropTypes.object,
  layouts: PropTypes.object,
  isChange: PropTypes.bool,
  placeholders: PropTypes.object,
  texts: PropTypes.object,
  images: PropTypes.object,
  allowRender: PropTypes.bool,
  lang: PropTypes.object,
  backgroundType: PropTypes.string,
};

export default connect(state => ({
  layouts: state.entities.layouts.byId,
  images: state.entities.images.byId,
  spine: state.entities.cover.spine,
  allowRender: state.editorUI.allowRender,
  bookProperties: state.bookProperties,
  autofillFinished: state.entities.io.autofillFinished,
  texts: state.entities.texts,
  backgroundType: state.editorUI.bookProperties.backgroundType,
}
))(Editor);



// WEBPACK FOOTER //
// ./src/components/editor/Editor.jsx